/**
 * main.ts — Brints Overview
 *
 * Handles:
 *  - Dynamic footer year
 *  - Sticky nav border-on-scroll
 *  - Active nav link scroll-spy (IntersectionObserver)
 *  - Mobile hamburger menu (keyboard / outside-click / Escape aware)
 *  - Scroll-reveal entrance animations (IntersectionObserver)
 *  - Hero word cycling animation
 */

'use strict';

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────

function qs<T extends Element>(selector: string, scope: ParentNode = document): T {
  const el = scope.querySelector<T>(selector);
  if (!el) throw new Error(`Element not found: ${selector}`);
  return el;
}

function qsa<T extends Element>(selector: string, scope: ParentNode = document): T[] {
  return Array.from(scope.querySelectorAll<T>(selector));
}

// ──────────────────────────────────────────────
// 1. FOOTER YEAR
// ──────────────────────────────────────────────
(function initFooterYear(): void {
  const el = document.getElementById('footer-year');
  if (el) el.textContent = String(new Date().getFullYear());
})();

// ──────────────────────────────────────────────
// 2. STICKY HEADER — add border once scrolled
// ──────────────────────────────────────────────
(function initStickyHeader(): void {
  const header = document.querySelector<HTMLElement>('.site-header');
  if (!header) return;

  const SCROLL_THRESHOLD = 10;

  function update(): void {
    header!.classList.toggle('scrolled', window.scrollY > SCROLL_THRESHOLD);
  }

  window.addEventListener('scroll', update, { passive: true });
  update();
})();

// ──────────────────────────────────────────────
// 3. MOBILE HAMBURGER MENU
// ──────────────────────────────────────────────
(function initMobileMenu(): void {
  const hamburger = document.querySelector<HTMLButtonElement>('.nav__hamburger');
  const navLinks   = document.querySelector<HTMLElement>('.nav__links');
  const header     = document.querySelector<HTMLElement>('.site-header');

  if (!hamburger || !navLinks) return;

  const MOBILE_BP = 640;

  function isOpen(): boolean {
    return hamburger!.getAttribute('aria-expanded') === 'true';
  }

  function openMenu(): void {
    hamburger!.setAttribute('aria-expanded', 'true');
    hamburger!.setAttribute('aria-label', 'Close navigation menu');
    navLinks!.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function closeMenu(restoreFocus: boolean = true): void {
    hamburger!.setAttribute('aria-expanded', 'false');
    hamburger!.setAttribute('aria-label', 'Open navigation menu');
    navLinks!.classList.remove('open');
    document.body.style.overflow = '';
    if (restoreFocus) {
      hamburger!.focus();
    }
  }

  hamburger.addEventListener('click', () => {
    isOpen() ? closeMenu() : openMenu();
  });

  // Close when a nav link is tapped
  qsa<HTMLAnchorElement>('.nav__link', navLinks).forEach(link => {
    link.addEventListener('click', () => {
      if (window.innerWidth <= MOBILE_BP) closeMenu();
    });
  });

  // Escape key
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen()) closeMenu();
  });

  // Outside click
  document.addEventListener('click', (e: MouseEvent) => {
    if (isOpen() && header && !header.contains(e.target as Node)) {
      closeMenu();
    }
  });

  // Close if viewport grows beyond mobile breakpoint
  window.addEventListener('resize', () => {
    if (window.innerWidth > MOBILE_BP && isOpen()) closeMenu();
  });
})();

// ──────────────────────────────────────────────
// 4. SCROLL-SPY — highlight active nav link
// ──────────────────────────────────────────────
(function initScrollSpy(): void {
  const sections   = qsa<HTMLElement>('section[id]');
  const navLinkEls = qsa<HTMLAnchorElement>('.nav__link');
  const header     = document.querySelector<HTMLElement>('.site-header');

  if (!sections.length || !navLinkEls.length) return;

  const navHeight = header?.offsetHeight ?? 64;

  const observer = new IntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        const id = (entry.target as HTMLElement).id;
        navLinkEls.forEach(link => {
          link.classList.toggle('active', link.getAttribute('href') === `#${id}`);
        });
      }
    },
    {
      root: null,
      rootMargin: `-${navHeight + 16}px 0px -60% 0px`,
      threshold: 0,
    }
  );

  sections.forEach(section => observer.observe(section));
})();

// ──────────────────────────────────────────────
// 5. SCROLL REVEAL — entrance animations
// ──────────────────────────────────────────────
(function initReveal(): void {
  const revealEls = qsa<HTMLElement>('.reveal');
  if (!revealEls.length) return;

  const observer = new IntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target); // animate once
        }
      }
    },
    {
      root: null,
      rootMargin: '0px 0px -80px 0px',
      threshold: 0.1,
    }
  );

  revealEls.forEach(el => observer.observe(el));
})();

// ──────────────────────────────────────────────
// 6. HERO WORD CYCLING
//    Cycles through descriptive words in the
//    hero title accent span
// ──────────────────────────────────────────────
(function initHeroWordCycle(): void {
  const accentEl = document.querySelector<HTMLSpanElement>('.hero__title--accent');
  if (!accentEl) return;

  const words: string[] = [
    'people love.',
    'teams trust.',
    'that matter.',
  ];

  let currentIndex = 0;
  const INTERVAL_MS = 3200;
  const FADE_MS     = 400;

  // Apply fade-out, swap text, fade-in
  function cycleWord(): void {
    accentEl!.style.transition = `opacity ${FADE_MS}ms ease-out`;
    accentEl!.style.opacity    = '0';

    setTimeout(() => {
      currentIndex = (currentIndex + 1) % words.length;
      accentEl!.textContent     = words[currentIndex];
      accentEl!.style.opacity   = '1';
    }, FADE_MS);
  }

  const timer = setInterval(cycleWord, INTERVAL_MS);

  // Stop cycling when user tabs away (performance)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearInterval(timer);
    }
  });
})();

// ──────────────────────────────────────────────
// 7. SMOOTH SCROLL — offset for sticky nav
// ──────────────────────────────────────────────
(function initSmoothScroll(): void {
  const header = document.querySelector<HTMLElement>('.site-header');

  qsa<HTMLAnchorElement>('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', (e: MouseEvent) => {
      const href = anchor.getAttribute('href');
      if (!href || href === '#') return;

      const target = document.querySelector<HTMLElement>(href);
      if (!target) return;

      e.preventDefault();
      const navHeight = header?.offsetHeight ?? 64;
      const top = target.getBoundingClientRect().top + window.scrollY - navHeight - 8;

      window.scrollTo({ top, behavior: 'smooth' });

      // Focus target element for screen readers and keyboard users
      if (target.hasAttribute('tabindex')) {
        target.focus({ preventScroll: true });
      }

      // Update URL hash without jumping
      history.pushState(null, '', href);
    });
  });
})();

// ──────────────────────────────────────────────
// 8. KAINOS SMART DOWNLOAD & METRICS (Layer 1 & 2)
// ──────────────────────────────────────────────
interface Window {
  umami?: {
    track: (
      eventName: string,
      eventData?: Record<string, string | number | boolean>
    ) => void;
  };
}

interface GitHubReleaseAsset {
  name: string;
  download_count: number;
  browser_download_url: string;
}

interface GitHubReleaseItem {
  tag_name: string;
  draft: boolean;
  prerelease: boolean;
  assets: GitHubReleaseAsset[];
}

interface ReleaseMetricsCache {
  timestamp: number;
  totalDownloads: number;
  latestTag: string;
  winUrl?: string | undefined;
  macUrl?: string | undefined;
  linuxUrl?: string | undefined;
}

(function initKainosSmartDownload(): void {
  type OSPlatform = 'windows' | 'macos' | 'linux';

  const METRICS_CACHE_KEY = 'kainos_download_metrics_cache_v1';
  const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour TTL to respect GitHub API rate limits

  // Helpers
  function trackDownloadEvent(platform: string, method: string, releaseTag: string): void {
    try {
      if (window.umami && typeof window.umami.track === 'function') {
        window.umami.track('kainos-download', {
          platform,
          method,
          release: releaseTag,
        });
      }
    } catch {
      // Analytics tracking is non-critical
    }
  }

  function getCachedMetrics(): ReleaseMetricsCache | null {
    try {
      const raw = localStorage.getItem(METRICS_CACHE_KEY);
      if (!raw) return null;
      const data: ReleaseMetricsCache = JSON.parse(raw);
      if (Date.now() - data.timestamp < CACHE_TTL_MS) {
        return data;
      }
    } catch {
      // Graceful fallback if localStorage is disabled or throws
    }
    return null;
  }

  function setCachedMetrics(metrics: Omit<ReleaseMetricsCache, 'timestamp'>): void {
    try {
      const data: ReleaseMetricsCache = {
        ...metrics,
        timestamp: Date.now(),
      };
      localStorage.setItem(METRICS_CACHE_KEY, JSON.stringify(data));
    } catch {
      // Ignore storage quota / privacy mode errors
    }
  }

  function parseReleaseData(releases: GitHubReleaseItem[]) {
    let totalDownloads = 0;
    for (const rel of releases) {
      if (Array.isArray(rel.assets)) {
        for (const asset of rel.assets) {
          totalDownloads += asset.download_count || 0;
        }
      }
    }

    const latest = releases.find(r => !r.draft && !r.prerelease) || releases[0];
    const latestTag = latest?.tag_name || 'v0.1.2';

    let winUrl: string | undefined;
    let macUrl: string | undefined;
    let linuxUrl: string | undefined;

    if (latest && Array.isArray(latest.assets)) {
      const exe = latest.assets.find(a => a.name.endsWith('-setup.exe') || a.name.endsWith('.exe'));
      if (exe) winUrl = exe.browser_download_url;

      const dmg = latest.assets.find(a => a.name.endsWith('.dmg'));
      if (dmg) macUrl = dmg.browser_download_url;

      const appImage = latest.assets.find(a => a.name.endsWith('.AppImage') || a.name.endsWith('.deb'));
      if (appImage) linuxUrl = appImage.browser_download_url;
    }

    return { totalDownloads, latestTag, winUrl, macUrl, linuxUrl };
  }

  function updateUI(
    platform: OSPlatform,
    latestTag: string,
    totalDownloads?: number,
    winUrl?: string,
    macUrl?: string,
    linuxUrl?: string
  ): void {
    const btn = document.getElementById('kainos-download-btn') as HTMLAnchorElement | null;
    const label = document.getElementById('kainos-download-label');
    const badge = document.getElementById('kainos-download-badge');
    const countEl = document.getElementById('kainos-download-count');
    const winLink = document.getElementById('kainos-link-win') as HTMLAnchorElement | null;
    const macLink = document.getElementById('kainos-link-mac') as HTMLAnchorElement | null;
    const linuxLink = document.getElementById('kainos-link-linux') as HTMLAnchorElement | null;

    // Update download count badge
    if (countEl && typeof totalDownloads === 'number' && totalDownloads >= 0) {
      countEl.textContent = totalDownloads.toLocaleString();
    }

    // Update alternative platform links
    if (winLink && winUrl) winLink.href = winUrl;
    if (macLink && macUrl) macLink.href = macUrl;
    if (linuxLink && linuxUrl) linuxLink.href = linuxUrl;

    if (!btn || !label || !badge) return;

    if (platform === 'macos') {
      if (macUrl) btn.href = macUrl;
      label.textContent = 'Download for macOS';
      badge.textContent = '.dmg';
      btn.setAttribute('aria-label', `Download Kainos for macOS (.dmg) ${latestTag}`);
      macLink?.classList.add('kainos-download__alt-link--current');
    } else if (platform === 'linux') {
      if (linuxUrl) btn.href = linuxUrl;
      label.textContent = 'Download for Linux';
      badge.textContent = '.AppImage';
      btn.setAttribute('aria-label', `Download Kainos for Linux (.AppImage) ${latestTag}`);
      linuxLink?.classList.add('kainos-download__alt-link--current');
    } else {
      // Default to Windows
      if (winUrl) btn.href = winUrl;
      label.textContent = 'Download for Windows';
      badge.textContent = '.exe (x64)';
      btn.setAttribute('aria-label', `Download Kainos for Windows (64-bit .exe) ${latestTag}`);
      winLink?.classList.add('kainos-download__alt-link--current');
    }
  }

  function setupClickTracking(
    platform: OSPlatform,
    getLatestTag: () => string
  ): void {
    const btn = document.getElementById('kainos-download-btn');
    const winLink = document.getElementById('kainos-link-win');
    const macLink = document.getElementById('kainos-link-mac');
    const linuxLink = document.getElementById('kainos-link-linux');

    btn?.addEventListener('click', () => {
      trackDownloadEvent(platform, 'primary-button', getLatestTag());
    });

    winLink?.addEventListener('click', () => {
      trackDownloadEvent('windows', 'alt-link', getLatestTag());
    });

    macLink?.addEventListener('click', () => {
      trackDownloadEvent('macos', 'alt-link', getLatestTag());
    });

    linuxLink?.addEventListener('click', () => {
      trackDownloadEvent('linux', 'alt-link', getLatestTag());
    });
  }

  // Detect client OS
  const ua = (navigator.userAgent || '').toLowerCase();
  const navData = (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData;
  const platformStr = (navData?.platform || navigator.platform || '').toLowerCase();

  const isMac = platformStr.includes('mac') || ua.includes('macintosh') || ua.includes('mac os');
  const isLinux = !isMac && (platformStr.includes('linux') || ua.includes('linux') || ua.includes('x11'));
  const detectedOS: OSPlatform = isMac ? 'macos' : isLinux ? 'linux' : 'windows';

  // Base fallback URLs
  const FALLBACK_TAG = 'v0.1.2';
  const FALLBACK_WIN = `https://github.com/Brints/kainos-releases/releases/download/${FALLBACK_TAG}/Kainos_0.1.2_x64-setup.exe`;
  const FALLBACK_MAC = `https://github.com/Brints/kainos-releases/releases/latest`;
  const FALLBACK_LINUX = `https://github.com/Brints/kainos-releases/releases/latest`;

  let currentTag = FALLBACK_TAG;

  // 1. Initial synchronous UI setup
  updateUI(detectedOS, currentTag, undefined, FALLBACK_WIN, FALLBACK_MAC, FALLBACK_LINUX);

  // 2. Setup click tracking listeners (Umami)
  setupClickTracking(detectedOS, () => currentTag);

  // 3. Check client-side cache
  const cached = getCachedMetrics();
  if (cached) {
    currentTag = cached.latestTag;
    updateUI(
      detectedOS,
      cached.latestTag,
      cached.totalDownloads,
      cached.winUrl || FALLBACK_WIN,
      cached.macUrl || FALLBACK_MAC,
      cached.linuxUrl || FALLBACK_LINUX
    );
    return;
  }

  // 4. Background fetch from GitHub API
  fetch('https://api.github.com/repos/Brints/kainos-releases/releases', {
    headers: {
      Accept: 'application/vnd.github.v3+json',
    },
  })
    .then(res => {
      if (!res.ok) throw new Error(`GitHub API HTTP ${res.status}`);
      return res.json() as Promise<GitHubReleaseItem[]>;
    })
    .then(releases => {
      if (!Array.isArray(releases) || releases.length === 0) return;
      const parsed = parseReleaseData(releases);
      currentTag = parsed.latestTag;

      setCachedMetrics({
        totalDownloads: parsed.totalDownloads,
        latestTag: parsed.latestTag,
        winUrl: parsed.winUrl,
        macUrl: parsed.macUrl,
        linuxUrl: parsed.linuxUrl,
      });

      updateUI(
        detectedOS,
        parsed.latestTag,
        parsed.totalDownloads,
        parsed.winUrl || FALLBACK_WIN,
        parsed.macUrl || FALLBACK_MAC,
        parsed.linuxUrl || FALLBACK_LINUX
      );
    })
    .catch(() => {
      // Graceful fallback: maintain existing fallback UI if network or rate limit fails
    });
})();
